import {Component, Input, OnInit} from '@angular/core';
import {CatalogService} from "../../../../../Services/CatalogManagement/catalog.service";
import {LoginService} from "../../../../../Services/login.service";
import {Catalog} from "../../../../../Model/Catalog/Catalog";
import {DataSourcesService} from "../../../../../Services/DataProcess/datasources.service";
import {ImportContainerService} from "../../../../../Services/DataProcess/import-container.service";
import {ImportContainer} from "../../../../../Model/dataProcess/importContainer";
import {ProductDataSource} from "../../../../../Model/dataProcess/productDataSource";
import {ManufacturerMappingService} from "../../../../../Services/DataProcess/manufacturerMapping.service";
import {ManufacturerMapping} from "../../../../../Model/dataProcess/manufacturerMapping";
import {SystemService} from "../../../../../Services/system.service";
import {TranslateService} from "@ngx-translate/core";
import {Address} from "../../../../../Model/Catalog/Address";
import {DataSourceMapping} from "../../../../../Model/Catalog/DataSourceMapping";
import {CountryCode} from "../../../../../Model/countryCode";

export interface DataSourcePart {

  name?: string | null;
  status: 'mapped' | 'unmapped' | 'none';
  selectedId?: string | null;
  readonly: boolean;
}

export interface CatalogMappingRow {
  id: number;
  importManufacturerName?: string;
  datasource?: DataSourcePart;
}

type FilterKey = 'showMapped' | 'showUnmapped';

@Component({
  selector: 'np-manufacture-mapping-tab',
  templateUrl: './np-manufacture-mapping-tab.component.html',
  styleUrl: './np-manufacture-mapping-tab.component.css',
  standalone: false
})
export class NpManufactureMappingTabComponent implements OnInit {

  @Input() container!: ImportContainer;

  importCatalogManufactureNames: string[] = [];
  importCatalog: Catalog = undefined;

  dataSources: ProductDataSource[] = []

  selectedComparisonCatalogId = "";
  selectedImportCatalogId = "";

  showMapped = true;
  showUnmapped = true;
  autoMappingMode = false;
  existingMappings: ManufacturerMapping[] = [];

  newAddedDatasource: ProductDataSource;
  types: string[] = ['Manufacturer', 'Supplier'];
  countryCodes: CountryCode[] = [];
  popupVisible: boolean = false;

  rows: CatalogMappingRow[] = [];
  allRows: CatalogMappingRow[] = [];

  constructor(
    public loginService: LoginService,
    public catalogService: CatalogService,
    public dataSourceService: DataSourcesService,
    public importContainerService: ImportContainerService,
    public manufactuerMappingService: ManufacturerMappingService,
    private systemService: SystemService,
    private translate: TranslateService,) {
    this.buildRows = this.buildRows.bind(this);
  }

  async ngOnInit() {
    await this.getAvailableDataSources();
    await this.getImportCatalog();
  }

  dropDownCfg = {
    toolbarItems: [
      {
        location: 'after',
        template: 'addSourceToolbarButton'
      }
    ]
  };

  async addNewSource() {
    var newDataSourceAddress = new Address();
    var newDataSourceImporterAddress = new Address();
    this.newAddedDatasource = {
      addressObj: newDataSourceAddress,
      importerAddress: newDataSourceImporterAddress
    } as ProductDataSource;
    this.popupVisible = true;
  }

  async onEditSaved($event) {
    this.dataSources = await this.dataSourceService.getAll(this.loginService.currentCustomer.id);
    this.ensureEmptyDataSource();
  }

  async getAvailableDataSources() {
    this.dataSources = await this.dataSourceService.getAll(this.loginService.currentCustomer.id);
    this.ensureEmptyDataSource();
  }

  private ensureEmptyDataSource(): void {
    const hasEmpty = this.dataSources.some(x => x.id == null || x.id === '');
    if (hasEmpty) return;

    const empty = { id: '', name: '' } as ProductDataSource;
    this.dataSources = [empty, ...this.dataSources];
  }

  get filteredRows(): CatalogMappingRow[] {
    return this.rows;
  }

  async getImportCatalogManufactureNames() {
    this.importCatalogManufactureNames = (await this.dataSourceService
      .getAllCatalogManufacturerNames(this.importCatalog.id, this.loginService.currentCustomer.id)).map(x => x.manufacturerName);

    this.buildRows();
  }

  buildRows() {
    this.allRows = [];

    if(this.importCatalogManufactureNames.length > 0){
      this.importCatalogManufactureNames.forEach((manufacturerName) => {
        let existingRow = this.allRows.find(x => x.importManufacturerName == manufacturerName);
        if(existingRow){
          existingRow.importManufacturerName = manufacturerName;
        }
        else {
          this.allRows.push({
            datasource: { name: "", status: "unmapped", selectedId: "", readonly: false} as DataSourcePart,
            id: Math.max(...this.allRows.map(r => r.id)) + 1,
            importManufacturerName: manufacturerName,
          });
        }
      })
    }
    if(this.existingMappings.length > 0){
      this.existingMappings.forEach((item) => {
        let existingRow = this.allRows.find(x => x.importManufacturerName == item.dataSourceMapping.manufacturerName);
        if(existingRow){
          existingRow.datasource.status = "mapped";
          existingRow.datasource.selectedId = item.dataSourceMapping.currentId;
        }
      });
    }
    this.allRows.forEach((row) => {
      if(row.importManufacturerName == "")
        row.datasource.readonly = true;
    });

    if(this.autoMappingMode)
      this.onAutoMap();

    this.filterRows();
  }

  private filterRows() {
    if (this.showMapped &&
      this.showUnmapped) {
      this.rows = [...this.allRows];
      return;
    }

    if (!this.showMapped &&
      !this.showUnmapped) {
      this.rows = [];
      return;
    }

    this.rows = this.allRows.filter(row =>
      (this.showMapped && row.datasource.status === "mapped") ||
      (this.showUnmapped && row.datasource.status === "unmapped")
    );
  }

  onAutoMap() {
    this.autoMappingMode = true;
    let allMapped = true;
    for(let row of this.allRows.filter(x => x.importManufacturerName != "" && x.datasource.status == "unmapped")){
      allMapped = false;
      let possibleDataSource =
        this.dataSources.find(x => x.name == row.importManufacturerName || x.alternativeNames?.includes(row.importManufacturerName));
      if(possibleDataSource){
        row.datasource.selectedId = possibleDataSource.id;
        row.datasource.name = possibleDataSource.name;
      }
    }
    if(allMapped)
      this.autoMappingMode = false;
  }

  async onDataSourceChanged(e: any, row: CatalogMappingRow) {
    if(!this.autoMappingMode || row.datasource.status == "mapped") {
      const newId = e.value;
      const oldId = e.previousValue;

      const datasourceId = newId;
      const manufacturerName = row.importManufacturerName;
      const catalogId = this.importCatalog.id;
      const existingMapping =
        this.existingMappings.find(x =>
          x.dataSourceMapping.manufacturerName == manufacturerName &&
          x.catalogId == catalogId
        );
      let existingMappingId = "";
      let originalId = datasourceId;
      if(existingMapping){
        existingMappingId = existingMapping.id;
        originalId = existingMapping.dataSourceMapping.originalId;
      }
      const dataSourceMapping = new DataSourceMapping(manufacturerName, originalId, datasourceId);
      const manufacturerMapping = new ManufacturerMapping(existingMappingId, this.container.id, catalogId, dataSourceMapping);
      if((datasourceId == null || datasourceId === '') && existingMappingId != "")
        await this.manufactuerMappingService.delete(manufacturerMapping, this.loginService.currentCustomer.id);
      else
        await this.manufactuerMappingService.update(manufacturerMapping, this.loginService.currentCustomer.id);
      await this.getExistingManufacturerMappings();
    }
  }

  async confirmRow(row: CatalogMappingRow) {
    const datasourceId = row.datasource.selectedId;
    if(datasourceId) {
      const manufacturerName = row.importManufacturerName;
      const catalogId = this.importCatalog.id;
      const dataSourceMapping = new DataSourceMapping(manufacturerName, datasourceId, datasourceId);
      const manufacturerMapping = new ManufacturerMapping("", this.container.id, catalogId, dataSourceMapping);

      await this.manufactuerMappingService.update(manufacturerMapping, this.loginService.currentCustomer.id);
      await this.getExistingManufacturerMappings();
    }
    else
      this.systemService.notifyInfo(this.translate.instant('Bitte einen Hersteller auswählen'))
  }

  private async getExistingManufacturerMappings() {
      this.existingMappings =
      await this.manufactuerMappingService.getAllManufacturerMappingsByCatalogId(this.importCatalog.id, this.loginService.currentCustomer.id);
      this.buildRows();
  }

  protected onToggleFilter(key: FilterKey, e: { value: boolean }) {
    this[key] = e.value;
    this.filterRows();
  }

  private async getImportCatalog() {
    this.catalogService.getCatalog(this.container.activeCatalogId, this.loginService.currentCustomer.id).subscribe(catalog => {
      this.importCatalog = catalog;
      this.getImportCatalogManufactureNames();
      this.getExistingManufacturerMappings();
    })
  }
}
